import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getUserRoles } from '../../../utils/userRoles';
import { fetchAssignedAdminTournaments } from '../../../services/tournamentAdminService';
import {
  buildManagementScope,
  buildTournamentAdminTournamentsPath,
} from '../../../utils/managementNavigation';
import TournamentList from '../../../components/common/lists/TournamentList';
import TableControls from '../../../components/common/TableControls';
import useTableControls from '../../../hooks/useTableControls';
import PageShell from '../../../components/layout/PageShell';

// ✅ Custom breadcrumbs example:
// const crumbs = [
//   { label: 'Home', to: '/' },
//   { label: 'Community', to: `/community/${communityId}` },
//   { label: 'Match Manager', to: `/community/${communityId}/match-manager/${managerId}` },
//   { label: 'Manual Match Format' },
// ];
// <PageShell breadcrumbs={crumbs}>...</PageShell>
// <PageShell breadcrumbOverrides={{ tournamentId: tournament?.name || 'Tournament' }}>
const TournamentAdminAssignedTournamentsPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const roles = getUserRoles(user);
  const canAccess = roles.has('tournament_admin') || roles.has('super_admin');

  useEffect(() => {
    const loadAssigned = async () => {
      if (!token || !canAccess) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError('');

      try {
        const response = await fetchAssignedAdminTournaments(token);
        setItems(Array.isArray(response) ? response : []);
      } catch (loadError) {
        setError(loadError?.message || 'Unable to load assigned tournaments.');
      } finally {
        setIsLoading(false);
      }
    };

    loadAssigned();
  }, [canAccess, token]);

  const tournamentSorters = useMemo(
    () => ({
      createdAt: (first, second) => new Date(first?.createdAt) - new Date(second?.createdAt),
      name: (first, second) =>
        (first?.name || 'Untitled tournament').localeCompare(second?.name || 'Untitled tournament', undefined, {
          sensitivity: 'base',
        }),
      status: (first, second) => Number(first?.isActive === false) - Number(second?.isActive === false),
    }),
    [],
  );

  const {
    paginatedItems: visibleTournaments,
    totalItems,
    totalPages,
    page,
    pageSize,
    sortKey,
    sortDirection,
    setSortKey,
    setSortDirection,
    setPage,
    setPageSize,
    searchTerm,
    setSearchTerm,
    toggleDirection,
  } = useTableControls(items, {
    initialSortKey: 'createdAt',
    initialSortDirection: 'desc',
    sorters: tournamentSorters,
    searchKeys: ['name', (tournament) => (tournament?.isActive !== false ? 'active' : 'archived')],
  });

  const getTournamentPath = (tournament) => {
    const scope = buildManagementScope({
      organizationId: tournament?.organizationId,
      clubId: tournament?.clubId,
      communityId: tournament?.communityId,
    });

    const adminId = user?.id || user?._id;
    const basePath = buildTournamentAdminTournamentsPath(scope, adminId);

    if (!basePath) return '/dashboard/individual/home';

    const tournamentId = tournament?._id?.toString?.() || tournament?._id;
    return tournamentId ? `${basePath}/${tournamentId}` : basePath;
  };

  // keep same behavior
  if (!token) return <Navigate to="/login" replace />;

  const hasStatusField = items.some((tournament) => typeof tournament?.isActive === 'boolean');

  return (
    <PageShell containerClassName="p-0">
    <div className="card shadow-sm border-0">
      <div className="card-body d-grid gap-4">
        {/* Top header: matches your other page style */}
        <div className="d-flex flex-column flex-lg-row justify-content-lg-between gap-2">
          <div>
            <p className="eyebrow text-primary mb-1">Tournament overview</p>
            <h1 className="h4 mb-2">My assigned tournaments</h1>
            <p className="text-muted mb-0">
              View the tournaments you manage and track their status from a single dashboard.
            </p>
          </div>

          <div className="d-flex align-items-start gap-2">
            <div className="d-flex flex-column text-end">
              <span className="fw-semibold">{totalItems} assigned</span>
              <span className="text-muted small">Page {page} of {totalPages}</span>
            </div>

            {/* Optional action button (UI only). You can change target if needed */}
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => navigate('/dashboard/individual/home')}
            >
              Back to home
            </button>
          </div>
        </div>

        {/* Access note */}
        {!canAccess && !isLoading ? (
          <div className="alert alert-warning py-2 mb-0">
            You don’t have permission to view assigned tournaments.
          </div>
        ) : null}

        {error && !isLoading ? <div className="alert alert-danger py-2 mb-0">{error}</div> : null}

        {/* Main list card (same style as your other page) */}
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-transparent border-0 pb-0">
            <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between gap-2">
              <div>
                <p className="text-muted small mb-1">Your tournaments</p>
                <h2 className="h5 mb-0">Review tournament assignments</h2>
              </div>
              <span className="badge bg-secondary-subtle text-secondary-emphasis">
                Tournament admin view
              </span>
            </div>
          </div>

          <div className="card-body d-grid gap-3 pt-3">
            <TableControls
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSortKeyChange={setSortKey}
              onSortDirectionChange={(direction) =>
                direction ? setSortDirection(direction) : toggleDirection()
              }
              sortOptions={[
                { value: 'createdAt', label: 'Created date' },
                { value: 'name', label: 'Name' },
                ...(hasStatusField ? [{ value: 'status', label: 'Status' }] : []),
              ]}
              page={page}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Search tournaments"
            />

            {/* List */}
            {!error ? (
              <TournamentList
                tournaments={visibleTournaments}
                isAdminView
                onTournamentClick={(tournament) => navigate(getTournamentPath(tournament))}
                emptyStateMessage={
                  searchTerm.trim()
                    ? `No tournaments match “${searchTerm.trim()}”.`
                    : 'No assigned tournaments found.'
                }
                isLoading={isLoading}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  
    </PageShell>
  );
};

export default TournamentAdminAssignedTournamentsPage;
